import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KpiDrillPanel } from "../../components/dashboard/KpiDrill";
import { Clock, RefreshCw } from "lucide-react";
import { ProcessFilters, applyFilters, Pager, EMPTY } from "../../components/filters/ProcessFilters";
import { api, errMsg } from "../../lib/api";
import { dateBR } from "../../lib/format";
import { PageHeader, Card, Loading, Pill } from "../../components/common";
import { Button } from "../../components/ui/button";
import { PHASE_CLS, WHO } from "../../components/admission/ReviewParts";
import { AdmissionReviewDialog } from "../../components/admission/AdmissionReviewDialog";

const ACTOR_CLS = { funcionario: "bg-sky-50 text-sky-700 border-sky-200", empresa: "bg-amber-50 text-amber-800 border-amber-300", dpdigi: "bg-violet-50 text-violet-700 border-violet-200" };
const ACTORS = [["empresa", "Empresa"], ["funcionario", "Funcionário"], ["dpdigi", "DPdigi"]];
const SIZE = 20;
const acc = (r) => ({ employee: r.employee_id, date: r.admission_date, requested: r.requested_at, status: r.phase, protocol: r.protocol, ref: (r.admission_date || "").slice(0, 7), actor: r.actor });
const REFRESH_MS = 15000;

// Live polling (15s + on focus) so the board reflects every step without reloading.
function useBoard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const inflight = useRef(false);
  const load = useCallback((silent) => {
    if (inflight.current) return;
    inflight.current = true; setBusy(true);
    api.get("/admissions/board", { silent: silent === true }).then((r) => { setD(r.data); setErr(""); })
      .catch((e) => setErr(errMsg(e))).finally(() => { inflight.current = false; setBusy(false); });
  }, []);
  useEffect(() => {
    load();
    const bg = () => document.visibilityState === "visible" && load(true);
    const t = setInterval(bg, REFRESH_MS);
    window.addEventListener("focus", bg); document.addEventListener("visibilitychange", bg);
    return () => { clearInterval(t); window.removeEventListener("focus", bg); document.removeEventListener("visibilitychange", bg); };
  }, [load]);
  return { d, err, busy, load };
}

export default function Admissions() {
  const { d, err, busy, load } = useBoard();
  const [phase, setPhase] = useState("");
  const [sp] = useSearchParams();
  const [f, setF] = useState(() => (sp.get("kpi") && sp.get("ref") ? { ...EMPTY, ref: sp.get("ref") } : EMPTY));
  const [page, setPage] = useState(1);
  const [rev, setRev] = useState(null);
  if (!d && err) return <Card className="p-10 text-center text-sm text-rose-700" data-testid="adm-board-error">{err}</Card>;
  if (!d) return <Loading />;
  const base = applyFilters(d.rows, f, acc);
  const counts = Object.fromEntries(Object.keys(d.labels).map((k) => [k, base.filter((r) => r.phase === k).length]));
  const all = base.filter((r) => !phase || r.phase === phase);
  const rows = all.slice((page - 1) * SIZE, page * SIZE);
  const emps = d.rows.map((r) => ({ id: r.employee_id, name: r.name })).sort((a, b) => a.name.localeCompare(b.name));
  const refs = [...new Set(d.rows.map((r) => (r.admission_date || "").slice(0, 7)).filter(Boolean))].sort().reverse();
  return (
    <div className="space-y-5">
      <KpiDrillPanel cards={["adm"]} />
      <PageHeader eyebrow="Admissões" title="Painel de admissões" subtitle={`Veja em que etapa cada admissão está e quem precisa agir agora${d.company ? ` · ${d.company}` : ""}.`} />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7" data-testid="adm-board-counts">
        {Object.entries(d.labels).map(([k, l], i) => (
          <button key={k} type="button" onClick={() => { setPhase(phase === k ? "" : k); setPage(1); }} data-testid={`adm-count-${k}`}
            className={`rounded-xl border bg-white p-3 text-left transition-shadow hover:shadow-sm ${phase === k ? "ring-2 ring-sky-500" : ""}`}>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Etapa {i + 1}</div>
            <div className="font-display text-2xl font-semibold" data-testid={`adm-count-value-${k}`}>{counts[k]}</div>
            <div className="text-[11px] leading-tight text-slate-500">{l}</div></button>))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <ProcessFilters value={f} onApply={(v) => { setF(v); setPage(1); }} employees={emps} refs={refs} statuses={Object.entries(d.labels)} actors={ACTORS} testid="adm-filters" />
        <span className="ml-auto flex items-center gap-2 text-xs text-slate-500">
          <span data-testid="adm-board-total">{all.length} de {d.rows.length} admissões</span>
          <span className="flex items-center gap-1" data-testid="adm-board-live" title="Atualiza automaticamente a cada 15 segundos">
            <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} />ao vivo · {new Date(d.generated_at).toLocaleTimeString("pt-BR")}</span>
        </span>
      </div>
      {err && <p className="text-xs text-rose-600" data-testid="adm-board-refresh-error">Falha ao atualizar: {err}. Mostrando os últimos dados carregados.</p>}
      <Card className="overflow-x-auto">
        {!rows.length ? <p className="p-10 text-center text-sm text-slate-500" data-testid="adm-board-empty">Nenhuma admissão para os filtros selecionados.</p> : (
          <table className="w-full text-sm" data-testid="adm-board-table">
            <thead className="bg-slate-50 text-xs text-slate-500"><tr>{["Funcionário", "Cargo", "Admissão", "Protocolo", "Etapa atual", "Quem deve agir", "Próxima ação", "Parado há", ""].map((h) => <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.employee_id} className="cursor-pointer border-t hover:bg-slate-50" onClick={() => setRev(r.employee_id)} data-testid={`adm-row-${r.employee_id}`}>
                <td className="px-4 py-2.5 font-medium"><Link to={`/empresa/funcionarios/${r.employee_id}`} onClick={(e) => e.stopPropagation()} className="hover:underline">{r.name}</Link></td>
                <td className="px-4 text-slate-600">{r.position || "—"}</td>
                <td className="px-4 font-mono text-xs whitespace-nowrap">{r.admission_date ? dateBR(r.admission_date) : "—"}{r.admission_date && <span className="ml-1 font-sans text-[10px] text-slate-400">{r.date_kind}</span>}</td>
                <td className="px-4 font-mono text-xs" data-testid={`adm-protocol-${r.employee_id}`}>{r.protocol || "—"}</td>
                <td className="px-4"><Pill cls={PHASE_CLS[r.phase]} testid={`adm-phase-${r.employee_id}`}>{r.phase_label}</Pill></td>
                <td className="px-4">{r.actor ? <Pill cls={ACTOR_CLS[r.actor]} testid={`adm-actor-cell-${r.employee_id}`}>{WHO[r.actor]}</Pill> : "—"}</td>
                <td className="px-4 text-xs text-slate-700" data-testid={`adm-next-${r.employee_id}`}>{r.next_action}</td>
                <td className={`px-4 text-xs whitespace-nowrap ${r.phase !== "concluida" && r.days >= 3 ? "font-semibold text-amber-700" : "text-slate-500"}`} data-testid={`adm-days-${r.employee_id}`}>
                  <Clock className="mr-1 inline h-3 w-3" />{r.days === 0 ? "hoje" : `${r.days} dia${r.days > 1 ? "s" : ""}`}</td>
                <td className="px-4"><Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setRev(r.employee_id); }} data-testid={`adm-review-${r.employee_id}`}>Detalhes</Button></td>
              </tr>))}</tbody>
          </table>)}
      </Card>
      <Pager page={page} total={all.length} size={SIZE} onPage={setPage} testid="adm-pager" />
      {rev && <AdmissionReviewDialog employeeId={rev} open={!!rev} onOpenChange={(o) => !o && setRev(null)} onChanged={load} />}
    </div>);
}
